import { Box, Chip, Typography, useTheme } from "@mui/material";
import { DownArrowIcon } from "components/icons";
import { memo, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getFullName } from "utilz/helpers";
import {
  type CcMember,
  SelectParticipantsDrawer,
} from "./SelectParticipantsDrawer";

export const AddParticipantComponent = memo(() => {
  const theme = useTheme();
  const { watch, setValue } = useFormContext();
  const showParticipants = watch("showParticipants");
  const customer = watch("customer");
  const ccMembers: CcMember[] = watch("ccMembers") || [];

  const [drawerOpen, setDrawerOpen] = useState(false);

  const customerId = customer?.mapping_id || "";
  const isDisabled = !customerId;

  const handleOpenDrawer = useCallback(() => {
    if (!isDisabled) {
      setDrawerOpen(true);
    }
  }, [isDisabled]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleAdd = useCallback(
    (members: CcMember[]) => {
      setValue("ccMembers", members);
    },
    [setValue],
  );

  const handleDeleteChip = useCallback(
    (userId: string) => {
      setValue(
        "ccMembers",
        ccMembers.filter((m) => m.user_id !== userId),
      );
    },
    [ccMembers, setValue],
  );

  if (!showParticipants) return null;

  return (
    <>
      <Box
        onClick={handleOpenDrawer}
        sx={{
          mt: "8px",
          position: "relative",
          minHeight: "32px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "4px",
          padding:
            ccMembers.length > 0 ? "4px 36px 4px 12px" : "0px 36px 0px 12px",
          borderRadius: "4px",
          border: `1px solid ${theme.palette.neutral["020"]}`,
          backgroundColor: "white",
          cursor: isDisabled ? "not-allowed" : "pointer",
          ...(isDisabled && {
            color: theme.palette.lightBg.high,
            borderColor: theme.palette.neutral["015"],
          }),
          "&:hover": isDisabled
            ? undefined
            : {
                borderColor: theme.palette.lightBg.medium,
              },
        }}
      >
        {/* Floating label */}
        <Typography
          component="span"
          sx={{
            position: "absolute",
            top: "-8px",
            left: "8px",
            px: "4px",
            backgroundColor: "white",
            color: theme.palette.lightBg.low,
            fontFamily: theme.typography["body-medium"].fontFamily,
            fontWeight: theme.typography["body-medium"].fontWeight,
            fontSize: theme.typography["body-medium"].fontSize,
            transform: "scale(0.75)",
            transformOrigin: "top left",
          }}
        >
          Participants
        </Typography>

        {/* Chips */}
        {ccMembers.map((member) => {
          const fullname =
            getFullName(member.first_name, "", member.last_name) ||
            member.display_name ||
            member.email;
          return (
            <Chip
              key={member.user_id}
              label={fullname}
              size="small"
              onDelete={(e) => {
                e.stopPropagation();
                handleDeleteChip(member.user_id);
              }}
              sx={{
                height: "24px",
                backgroundColor: theme.palette.neutral["005"],
                borderRadius: "4px",
                border: "none",
                "& .MuiChip-label": {
                  ...theme.typography["caption-xs"],
                  color: theme.palette.lightBg.medium,
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: "12px",
                  color: theme.palette.lightBg.low,
                  "&:hover": {
                    color: theme.palette.lightBg.medium,
                  },
                },
              }}
            />
          );
        })}

        {/* Dropdown arrow */}
        <DownArrowIcon
          fontSize="small"
          htmlColor={theme.palette.lightBg.low}
          sx={{
            position: "absolute",
            right: "8px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      </Box>

      <SelectParticipantsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        customerId={customerId}
        selectedMembers={ccMembers}
        onAdd={handleAdd}
      />
    </>
  );
});

AddParticipantComponent.displayName = "AddParticipantComponent";
